import { resetPassword } from '../http';
import { FormEvent, ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmPasswordReset } from '../http';
import { checkValidation } from '../util/functions';
import { AlertI, Button } from './Button';
import { InputField } from './InputField';
import { PasswordUI } from './PasswordUI';
import { Pin } from './Pin';
import { FormattedMessage } from 'react-intl';

interface Props {
  setStep: (step: number) => void;
}

export interface ResetDataI {
  pin: string;
  password: string;
  confirmPassword: string;
}

export interface ErrorI {
  pin?: string | null;
  password?: string | null;
  confirmPassword?: string | null;
}

export default function ConfirmPassword(props: Props): ReactElement {
  const [error, setError] = useState<ErrorI>({});
  const [passwordStrength, setPasswordStrength] = useState<number>(0);
  const [verifyEmailSent, setVerifyEmailSent] = useState<boolean>(false);
  const [resetData, setResetData] = useState<ResetDataI>({
    pin: '',
    password: '',
    confirmPassword: '',
  });
  const [alert, setAlert] = useState<AlertI | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [pinMatched] = useState<boolean>(true);
  const [triggerResendVerify, setTriggerResendVerify] = useState<boolean>(false);

  const handleVerify = (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    const catchError = checkValidation(resetData);
    let failed = false;
    //checking if any other error has occurred
    Object.keys(error).map((key) => {
      if (error[key] !== null) {
        failed = true;
      }
      return key;
    });
    if (failed || passwordStrength < 3) {
      setError(catchError);
      return;
    } else {
      setTriggerResendVerify(!triggerResendVerify);
      //api request
      setLoading(true);
      confirmPasswordReset(resetData)
        .then((res) => {
          setLoading(false);
          localStorage.removeItem('step');
          navigate('/', { replace: true });
        })
        .catch(function (err) {
          const error = err?.response?.data;
          setAlert({
            type: 'error',
            message: error.message,
          });
          setLoading(false);
        });
    }
  };

  const onResendVerifyEmail = () => {
    //Resend Verify Email api request goes here
    try {
      const email = localStorage.getItem('email');
      if (email) {
        resetPassword(email).then((res) => {
          setVerifyEmailSent(true);
          setTriggerResendVerify(!triggerResendVerify);
          localStorage.removeItem('pin_time');
        });
      }
    } catch (e) {
      setVerifyEmailSent(true);
    }
  };

  return (
    <div>
      <Pin triggerResendVerify={triggerResendVerify} />
      <form className="form-container" onSubmit={handleVerify}>
        <div>
          <InputField
            type="text"
            onChange={(e) => {
              if (e.target.value.length) {
                setError({ ...error, pin: null });
              } else {
                setError({ ...error, pin: 'pinAlert' });
              }
              if (alert) {
                setAlert(null);
              }

              setResetData({ ...resetData, pin: e.target.value });
            }}
            value={resetData.pin}
            placeholder="Enter PIN"
          />
          <span className="error">{error?.pin ? <FormattedMessage id={error?.pin} /> : null}</span>
        </div>
        <div className="flex-b" style={{ width: '85%' }}>
          <div className="text-btn" onClick={onResendVerifyEmail}>
            <span>
              <FormattedMessage id="sendVerificationPinAgain" />
            </span>
          </div>
          <div
            className="text-btn"
            onClick={() => {
              localStorage.removeItem('step');
              localStorage.removeItem('email');
              localStorage.removeItem('pin_time');
              props.setStep(1);
            }}
          >
            <span>
              <FormattedMessage id="cancel" />{' '}
            </span>
          </div>
        </div>

        {pinMatched ? (
          <PasswordUI
            data={resetData}
            error={error}
            onPasswordChange={(password, strength) => {
              setPasswordStrength(strength);

              if (password.length) {
                if (strength < 3) {
                  setError({ ...error, password: 'passwordStrongAlert' });
                } else {
                  setError({ ...error, password: null });
                }
              } else {
                setError({ ...error, password: 'requiredAlert' });
              }
              setResetData({ ...resetData, password: password });
            }}
            onConfirmPasswordChange={(password) => {
              setResetData({ ...resetData, confirmPassword: password });
              if (password.length) {
                setError({ ...error, confirmPassword: null });
              } else {
                setError({ ...error, confirmPassword: 'requiredAlert' });
              }
            }}
          />
        ) : null}
        <div className="mb-15">
          <Button loading={loading} disabled={loading} alert={alert}>
            <FormattedMessage id="updatePassword" />
          </Button>
          {verifyEmailSent ? (
            <div style={{ width: '85%', marginTop: '5px' }}>
              <span className="text-btn">
                <FormattedMessage id="verificationPinSentAlert" />
              </span>
            </div>
          ) : null}
        </div>
      </form>
    </div>
  );
}
